/**
 * Engine creation: the only place a Babylon engine is constructed (guarded pattern from
 * jreb-sailing). WebGPU attempt under one timeout, crash memory in localStorage, WebGL2 fallback.
 * URL override: ?gfx=auto (default) | webgpu | webgl2.
 */
import { Engine } from '@babylonjs/core/pure';
import type { AbstractEngine, WebGPUEngine } from '@babylonjs/core/pure';
import { registerSceneFeatures, registerWebGL2Engine } from './babylon';
import {
  CRASH_FLAG_KEY,
  WEBGPU_PAINTED_KEY,
  describeGfxFailure,
  formatGfxFlag,
  formatPaintMarker,
  parseGfxFlag,
  parsePaintMarker,
  shouldBlockWebGPU,
} from './gfxMemory';
import type { RememberedGfxFailure } from './gfxMemory';

export type GfxBackend = 'webgpu' | 'webgl2';
export type GfxPreference = 'auto' | GfxBackend;

export interface CreatedEngine {
  engine: AbstractEngine;
  isWebGPU: boolean;
  backend: GfxBackend;
  backendLabel: string;
  gpuInfo: string;
  fallbackReason: string;
}

const WEBGPU_INIT_TIMEOUT_MS = 5000;
const STABLE_SECONDS_TO_CLEAR_CRASH_FLAG = 5;
const STABLE_FRAMES_TO_CLEAR_CRASH_FLAG = 30;

export function readGfxPreference(search: string = location.search): GfxPreference {
  const value = new URLSearchParams(search).get('gfx');
  return value === 'webgpu' || value === 'webgl2' ? value : 'auto';
}

export async function createEngine(canvas: HTMLCanvasElement): Promise<CreatedEngine> {
  registerSceneFeatures();
  const preference = readGfxPreference();
  const antialias = true;

  let fallbackReason = '';
  if (preference !== 'webgl2') {
    const remembered = preference === 'auto' ? readCrashFlag() : null;
    if (remembered) {
      fallbackReason = describeGfxFailure(remembered);
    } else if (!('gpu' in navigator) || !navigator.gpu) {
      fallbackReason = 'WebGPU is not exposed (navigator.gpu missing; it also needs a secure context)';
    } else {
      writeCrashFlag(formatGfxFlag(Date.now()));
      const { tryCreateWebGPU } = await import('./engine.webgpu');
      const attempt = await tryCreateWebGPU(canvas, antialias, WEBGPU_INIT_TIMEOUT_MS);
      if (attempt.engine) {
        clearCrashFlagWhenStable(attempt.engine);
        return {
          engine: attempt.engine,
          isWebGPU: true,
          backend: 'webgpu',
          backendLabel: 'WebGPU',
          gpuInfo: describeGpu(attempt.engine),
          fallbackReason: '',
        };
      }
      fallbackReason = attempt.reason;
      writeCrashFlag(formatGfxFlag(Date.now(), attempt.reason));
    }
    console.warn(`[engine] Using WebGL2: ${fallbackReason}`);
  }

  registerWebGL2Engine();
  const engine = new Engine(
    canvas,
    antialias,
    {
      stencil: true,
      alpha: false,
      premultipliedAlpha: false,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
      audioEngine: false,
    },
    false, // adaptToDeviceRatio: the quality controller applies its own tier-capped scaling
  );
  if (engine.webGLVersion < 2) {
    console.error('[engine] WebGL2 is not available; the game needs WebGL2 or WebGPU.');
  }
  return {
    engine,
    isWebGPU: false,
    backend: 'webgl2',
    backendLabel: fallbackReason ? `WebGL2 (fallback: ${fallbackReason})` : 'WebGL2',
    gpuInfo: describeGpu(engine),
    fallbackReason,
  };
}

function describeGpu(engine: Engine | WebGPUEngine): string {
  try {
    const info = engine.getInfo();
    return [info.vendor, info.renderer].filter(Boolean).join(' · ');
  } catch {
    return '';
  }
}

function readCrashFlag(): RememberedGfxFailure | null {
  try {
    const flag = parseGfxFlag(localStorage.getItem(CRASH_FLAG_KEY));
    if (!flag) {
      localStorage.removeItem(CRASH_FLAG_KEY);
      return null;
    }
    if (!shouldBlockWebGPU(flag, parsePaintMarker(localStorage.getItem(WEBGPU_PAINTED_KEY)))) {
      // Audit #6: painted then closed during the stable window — a tab-close artefact, not a crash.
      // Clear both keys and let the WebGPU attempt run again.
      localStorage.removeItem(CRASH_FLAG_KEY);
      localStorage.removeItem(WEBGPU_PAINTED_KEY);
      return null;
    }
    return flag;
  } catch {
    return null; // localStorage unavailable (private mode): behave as no memory
  }
}

function writeCrashFlag(value: string): void {
  try {
    localStorage.setItem(CRASH_FLAG_KEY, value);
  } catch {
    /* ignore */
  }
}

/** Audit #6: stamps the first presented frame so a later tab-close is not mistaken for a crash. */
function writePaintMarker(): void {
  try {
    localStorage.setItem(WEBGPU_PAINTED_KEY, formatPaintMarker(Date.now()));
  } catch {
    /* ignore */
  }
}

function clearCrashFlagWhenStable(engine: AbstractEngine): void {
  let frames = 0;
  let cleared = false;
  const observer = engine.onEndFrameObservable.add(() => {
    frames++;
    if (frames === 1) writePaintMarker();
    if (frames < STABLE_FRAMES_TO_CLEAR_CRASH_FLAG || cleared) return;
    cleared = true;
    engine.onEndFrameObservable.remove(observer);
    try {
      localStorage.removeItem(CRASH_FLAG_KEY);
    } catch {
      /* ignore */
    }
  });
  window.setTimeout(() => {
    if (document.hidden) {
      // Timer fired while hidden: rAF never runs, so the page never crashed. Don't punish the next visit.
      try { localStorage.removeItem(CRASH_FLAG_KEY); } catch { /* ignore */ }
      return;
    }
    if (frames >= STABLE_FRAMES_TO_CLEAR_CRASH_FLAG || cleared) return;
    engine.onEndFrameObservable.remove(observer);
    // Still unstable after the window: leave the flag; a crash here is what the flag is for.
  }, (STABLE_SECONDS_TO_CLEAR_CRASH_FLAG + 2) * 1000);
}
